import { createContext, useContext, useEffect, useMemo, useState, type PropsWithChildren } from 'react';
import type { Hazard } from '../../mocks/fixtures/safety';

export type HazardReport = {
  code: string;
  location: string;
  description: string;
  category: string;
  reportMode: '实名' | '匿名';
  target: '向隐患单位报告' | '向集团报告';
  org: string;
  reportTime: string;
  status: '草稿' | '待核查' | '核查中' | '属实已转隐患' | '不属实已驳回' | '已撤回';
  rewardStatus: string;
  level?: '一般' | '重大';
  hazardCode?: string;
  resultNote?: string;
};

type NewReport = Pick<HazardReport, 'location' | 'description' | 'category' | 'reportMode' | 'target' | 'org'>;
type NewHazard = Pick<Hazard, 'name' | 'source' | 'level' | 'org'> & Partial<Pick<Hazard, 'deadline' | 'owner'>>;

type PreventionStoreValue = {
  generatedHazards: Hazard[];
  reports: HazardReport[];
  submitReport: (report: NewReport, draft?: boolean) => HazardReport;
  acceptReport: (code: string) => void;
  withdrawReport: (code: string) => void;
  extendReport: (code: string, note: string) => void;
  concludeReport: (code: string, truthful: boolean, level: '一般' | '重大', note: string) => void;
  advanceReward: (code: string, status: string) => void;
  createHazard: (hazard: NewHazard) => Hazard;
  updateGeneratedHazard: (code: string, patch: Partial<Hazard>) => void;
};

const STORAGE_KEY = 'safety-prevention-workflow-v1';

const seedReports: HazardReport[] = [
  { code: 'YHBG-2026-0071', location: 'G90 K1660+200 养护作业区', description: '作业区锥桶摆放间距过大，未按规范布设过渡区。', category: '养护作业', reportMode: '实名', target: '向隐患单位报告', org: '宁远营运中心', reportTime: '2026-07-15 08:45', status: '待核查', rewardStatus: '待资格判定' },
  { code: 'YHBG-2026-0070', location: '岚城连接线 K12+300 急弯路段', description: '急弯处警示标志被树枝遮挡，夜间反光效果差。', category: '道路交通设施', reportMode: '匿名', target: '向隐患单位报告', org: '岚城营运中心', reportTime: '2026-07-14 17:20', status: '待核查', rewardStatus: '待资格判定' },
  { code: 'YHBG-2026-0069', location: '百花高架桥伸缩缝', description: '车辆通过时伸缩缝异响明显，疑似锚固松动。', category: '桥梁构造物', reportMode: '实名', target: '向集团报告', org: '百花营运中心', reportTime: '2026-07-14 10:05', status: '待核查', rewardStatus: '待资格判定' },
  { code: 'YHBG-2026-0068', location: 'G9001 K78+300 隧道口边坡', description: '强降雨后坡面有新裂缝并伴有少量落石。', category: '边坡与地质灾害', reportMode: '实名', target: '向集团报告', org: '云州营运中心', reportTime: '2026-07-13 09:28', status: '核查中', rewardStatus: '待资格判定', level: '重大' },
  { code: 'YHBG-2026-0067', location: '澜江特大桥伸缩缝', description: '车辆通过时伸缩缝异响明显，疑似锚固松动。', category: '桥梁构造物', reportMode: '实名', target: '向隐患单位报告', org: '澜江营运中心', reportTime: '2026-07-12 15:10', status: '核查中', rewardStatus: '待资格判定' },
  { code: 'YHBG-2026-0066', location: '沙湾收费站外广场', description: '防撞桶破损缺失，夜间存在安全隐患。', category: '交通安全设施', reportMode: '匿名', target: '向隐患单位报告', org: '沙湾营运中心', reportTime: '2026-07-11 19:36', status: '核查中', rewardStatus: '待资格判定' },
  { code: 'YHBG-2026-0065', location: '清溪服务区配电房', description: '线缆绝缘层老化并有裸露情况。', category: '用电安全', reportMode: '匿名', target: '向隐患单位报告', org: '河阳营运中心', reportTime: '2026-07-11 16:40', status: '属实已转隐患', rewardStatus: '待身份确认', level: '一般', hazardCode: 'HYGS-2026-YH-0329' },
  { code: 'YHBG-2026-0061', location: '龙洞堡收费站广场', description: '收费岛警示柱松动。', category: '道路交通设施', reportMode: '实名', target: '向隐患单位报告', org: '云州营运中心', reportTime: '2026-07-09 08:12', status: '不属实已驳回', rewardStatus: '不予奖励', resultNote: '现场复核警示柱连接牢固，影像为维修前历史照片。' },
];

function readState(): { generatedHazards: Hazard[]; reports: HazardReport[] } {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as { generatedHazards: Hazard[]; reports: HazardReport[] };
  } catch {
    // Storage is an enhancement; the in-memory workflow remains usable.
  }
  return { generatedHazards: [], reports: seedReports };
}

const PreventionStore = createContext<PreventionStoreValue | null>(null);

export function PreventionProvider({ children }: PropsWithChildren) {
  const [initial] = useState(readState);
  const [generatedHazards, setGeneratedHazards] = useState<Hazard[]>(initial.generatedHazards);
  const [reports, setReports] = useState<HazardReport[]>(initial.reports);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ generatedHazards, reports }));
    } catch {
      // Keep the prototype functional when storage is blocked.
    }
  }, [generatedHazards, reports]);

  function createHazard(input: NewHazard) {
    const sequence = 400 + generatedHazards.length + 1;
    const hazard: Hazard = {
      code: `HYGS-2026-YH-${String(sequence).padStart(4, '0')}`,
      name: input.name,
      source: input.source,
      level: input.level,
      org: input.org,
      foundAt: '07-14',
      deadline: input.deadline ?? '07-25',
      stage: 1,
      status: '待整改分派',
      owner: input.owner ?? '—',
    };
    setGeneratedHazards((current) => [hazard, ...current]);
    return hazard;
  }

  function submitReport(input: NewReport, draft = false) {
    const report: HazardReport = {
      ...input,
      code: `YHBG-2026-${String(70 + reports.length).padStart(4, '0')}`,
      reportTime: '2026-07-14 14:00',
      status: draft ? '草稿' : '待核查',
      rewardStatus: '待核查',
    };
    setReports((current) => [report, ...current]);
    return report;
  }

  function patchReport(code: string, patch: Partial<HazardReport>) {
    setReports((current) => current.map((report) => report.code === code ? { ...report, ...patch } : report));
  }

  function acceptReport(code: string) {
    patchReport(code, { status: '核查中' });
  }

  function withdrawReport(code: string) {
    patchReport(code, { status: '已撤回' });
  }

  function extendReport(code: string, note: string) {
    patchReport(code, { status: '核查中', resultNote: `已延期：${note}` });
  }

  function concludeReport(code: string, truthful: boolean, level: '一般' | '重大', note: string) {
    const report = reports.find((item) => item.code === code);
    if (!report) return;
    if (!truthful) {
      patchReport(code, { status: '不属实已驳回', rewardStatus: '不予奖励', resultNote: note });
      return;
    }
    const hazard = createHazard({ name: report.description, source: `隐患报告 · ${code}`, level, org: report.org });
    patchReport(code, { status: '属实已转隐患', rewardStatus: '待资格判定', level, hazardCode: hazard.code, resultNote: note });
  }

  function advanceReward(code: string, status: string) {
    patchReport(code, { rewardStatus: status });
  }

  function updateGeneratedHazard(code: string, patch: Partial<Hazard>) {
    setGeneratedHazards((current) => current.map((hazard) => hazard.code === code ? { ...hazard, ...patch } : hazard));
  }

  const value = useMemo<PreventionStoreValue>(() => ({
    generatedHazards,
    reports,
    submitReport,
    acceptReport,
    withdrawReport,
    extendReport,
    concludeReport,
    advanceReward,
    createHazard,
    updateGeneratedHazard,
  // Functions intentionally close over the latest state and are refreshed with it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [generatedHazards, reports]);

  return <PreventionStore.Provider value={value}>{children}</PreventionStore.Provider>;
}

export function usePreventionStore() {
  const value = useContext(PreventionStore);
  if (!value) throw new Error('usePreventionStore 必须在 PreventionProvider 内使用');
  return value;
}
